<template>
  <div>
    <va-card>
      <va-card-title>
        <slot name="title">
          <div class="title-container">
            <div>
              {{ $t("organizations.title") }}
            </div>
            <va-button
              class="ml-2"
              @click="openCreateOrganizationModal"
              color="primary"
            >
              {{ $t("organizations.create") }}
            </va-button>
          </div>
        </slot>
      </va-card-title>

      <!-- Table -->
      <va-card-content>
        <va-data-table
          :no-data-html="$t('organizations.noResults')"
          :items="state.organizations"
        >
          <template #header(organization)>{{
            $t("organizations.name")
          }}</template>
          <template #header(id)>{{ $t("organizations.id") }}</template>
          <template #header(createdAt)>
            {{ $t("organizations.createdAt") }}
          </template>

          <template #cell(organization)="{ source: org }">
            <router-link :to="{ path: organizationLink(org.id) }">
              {{ org.name }}
            </router-link>
          </template>

          <template #cell(id)="{ source: id }">
            <router-link :to="{ path: organizationLink(id) }">
              {{ id }}
            </router-link>
            <button-clipboard-copy
              :field="$t('organizations.id')"
              :value="id"
            />
          </template>

          <template #cell(createdAt)="{ source: createdAt }">
            <td>
              {{ printDate(createdAt) }}
            </td>
          </template>
        </va-data-table>
      </va-card-content>
      <va-card-content>
        <va-pagination
          class="pagination"
          input
          color="#000000"
          :hide-on-single-page="true"
          v-model="state.currentPage"
          :pages="state.totalPages"
          :page-size="state.perPage"
        />
      </va-card-content>
    </va-card>
    <create-organization-model
      :is-open="state.showCreateOrganizationModal"
      :show-invitation-option="true"
      @cancel="cancelCreateOrganization"
      @create="createOrganization"
    />
  </div>
</template>

<script setup>
import { reactive, watch, onMounted, inject, defineExpose } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import {
  callFetchOrganizations,
  callCreateOrganization,
  callFetchOrganization
} from "@/api/identity";
import ButtonClipboardCopy from "@/components/ButtonClipboardCopy.vue";
import { organizationLink } from "@/utils/identity";
import { printDate } from "@/utils";
import CreateOrganizationModel from "@/components/modal/CreateOrganizationModel.vue";

const router = useRouter();
const store = useStore();
const { t } = useI18n();
const handleError = inject("handleError");
const toasts = inject("toasts");

const state = reactive({
  organizations: [],
  loading: false,
  lastLoadedPage: undefined,
  currentPage: 1,
  totalItems: 0,
  perPage: 100,
  totalPages: 0,
  showCreateOrganizationModal: false
});

const props = defineProps({
  params: {
    type: Object
  }
});

/**
 * Create the parameters object for the request and call it.
 * Then calculate the page properties's values.
 * @param {number} page - positive integer
 */
async function loadOrganizations(page = 1) {
  if (state.loading === true || page === state.lastLoadedPage) {
    return;
  }

  state.lastLoadedPage = page;
  state.loading = true;

  const params = buildUrlParams(page);

  try {
    const resp = await callFetchOrganizations({ params });

    state.organizations = resp.data.items;
    state.organizations = resp.data.items.map(item => {
      return {
        organization: {
          name: item.name,
          id: item.id
        },
        id: item.id,
        createdAt: item.createdAt
      };
    });

    state.totalItems = resp.data.total;
    state.currentPage = page;
    state.totalPages =
      Math.floor(resp.data.total / state.perPage) +
      (resp.data.total % state.perPage === 0 ? 0 : 1);
  } catch (e) {
    handleError(e);
  } finally {
    state.loading = false;
  }
}

function buildUrlParams(page = 1) {
  const params = new URLSearchParams();

  for (const key of Object.keys(props.params)) {
    if (props.params[key] !== undefined) {
      if (Array.isArray(props.params[key])) {
        for (const p of props.params[key]) {
          params.append(key, p);
        }
      } else {
        params.append(key, props.params[key]);
      }
    }
  }

  params.set("count", state.perPage);
  params.set("page", page);

  return params;
}

watch(
  () => state.currentPage,
  async currentPage => {
    await loadOrganizations(currentPage);
  }
);

watch(
  () => props.update,
  async () => {
    await loadOrganizations();
  }
);

onMounted(async () => {
  await loadOrganizations();
});

const openCreateOrganizationModal = () => {
  state.showCreateOrganizationModal = true;
};

const cancelCreateOrganization = () => {
  state.showCreateOrganizationModal = false;
};

const createOrganization = async organizationName => {
  try {
    const currentUser = store.state.identity.user;
    if (!currentUser?.id) {
      throw new Error("User not found");
    }

    console.log("Creating organization with data:", organizationName);
    console.log("Current user ID:", currentUser.id);

    // Create the organization
    const response = await callCreateOrganization({
      name: organizationName,
      ownerUserId: currentUser.id
    });
    console.log("Organization creation response:", response);

    if (!response?.data?.id) {
      throw new Error("Organization creation failed - no ID returned");
    }

    const organizationId = response.data.id;
    console.log("Created organization ID:", organizationId);

    await callFetchOrganization(organizationId);

    state.showCreateOrganizationModal = false;
    toasts.info(t("organizations.creationSuccess"));

    // Rediriger vers la page de l'organisation avec les paramètres nécessaires
    router.push({
      path: `/admin/identity/organizations/${organizationId}`
    });
  } catch (error) {
    console.error("Error in createOrganization:", error);
    state.showCreateOrganizationModal = false;

    let errorMessage = "Error creating organization";
    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    toasts.error(errorMessage);
  }
};

defineExpose({
  loadOrganizations
});
</script>

<style lang="scss" scoped>
.title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
</style>
